import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { usePdaScan, isInPdaWrapper } from "../../hooks/usePdaScan";
import { BleReaderButton } from "@/components/BleReaderButton";
import { usePdaScanReset } from "../../hooks/usePdaScanReset";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Inbox, Scan, Trash2, AlertCircle, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBTABS = [
  { key: "receive", label: "Receive — Dirty", icon: Inbox },
  { key: "rewash",  label: "Rewash — Not clean", icon: RotateCcw },
];

export default function ReceivingTab() {
  const [tab, setTab] = useState("receive");
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-brand-navy tracking-tight">Receiving</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Scan returning RFIDs into the warehouse, or process rewash items that came back dirty.
        </p>
      </div>
      <div className="flex gap-1 border-b border-neutral-200">
        {SUBTABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`receiving-subtab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors -mb-px ${
                active ? "border-brand-navy text-brand-navy" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "receive" && <ReceiveView />}
      {tab === "rewash"  && <RewashView />}
    </div>
  );
}

// Look up an RFID -> { rfid, product_id, product_name } or marks unknown.
async function lookupRfid(code) {
  try {
    const res = await axios.get(`${API}/linen?search=${encodeURIComponent(code)}`);
    const items = res.data?.items || res.data || [];
    const exact = items.find((it) => it.rfid === code);
    if (exact) {
      return { rfid: code, product_id: exact.product_id, product_name: exact.product_name };
    }
  } catch {
    // fall through — treat as unknown so user can still queue it
  }
  return { rfid: code, product_id: null, product_name: null };
}

// ---------- Normal receive (returned clean / for inventory) ----------
function ReceiveView() {
  const [scanInput, setScanInput] = useState("");
  const [scanned, setScanned] = useState([]); // [{rfid, product_id, product_name}]
  const [submitting, setSubmitting] = useState(false);
  const [unknownList, setUnknownList] = useState([]);
  const inputRef = useRef(null);
  const inFlightRef = useRef(new Set());

  const handleScan = (e) => {
    e?.preventDefault?.();
    const code = scanInput.trim();
    if (!code) return;
    setScanInput("");
    if (scanned.find((s) => s.rfid === code)) {
      toast.warning(`${code} already in list`);
      setTimeout(() => inputRef.current?.focus(), 10);
      return;
    }
    // PDA burst speed: push immediately with no product info, resolve in background.
    setScanned((prev) => {
      if (prev.find((s) => s.rfid === code)) return prev;
      return [...prev, { rfid: code, product_id: null, product_name: null }];
    });
    if (!inFlightRef.current.has(code)) {
      inFlightRef.current.add(code);
      lookupRfid(code).then((entry) => {
        inFlightRef.current.delete(code);
        if (entry.product_id) {
          setScanned((prev) => prev.map((s) => s.rfid === code
            ? { ...s, product_id: entry.product_id, product_name: entry.product_name }
            : s));
        }
      });
    }
    setTimeout(() => inputRef.current?.focus(), 10);
  };

  // Bottom-strip Reset wipes our scanned list too — so "scanned the wrong
  // batch by mistake" doesn't require manual list editing.
  usePdaScanReset(() => { setScanned([]); setUnknownList([]); });

  // Native PDA wrapper: route the "Stop & Send" payload into our scanned list.
  usePdaScan((epcs) => {
    setScanned((prev) => {
      const known = new Set(prev.map((s) => s.rfid));
      const additions = [];
      epcs.forEach((rfid) => {
        if (!known.has(rfid)) {
          known.add(rfid);
          additions.push({ rfid, product_id: null, product_name: null });
          if (!inFlightRef.current.has(rfid)) {
            inFlightRef.current.add(rfid);
            lookupRfid(rfid).then((entry) => {
              inFlightRef.current.delete(rfid);
              if (entry.product_id) {
                setScanned((p) => p.map((s) => s.rfid === rfid
                  ? { ...s, product_id: entry.product_id, product_name: entry.product_name }
                  : s));
              }
            });
          }
        }
      });
      return additions.length ? [...prev, ...additions] : prev;
    });
  });

  const remove = (c) => setScanned((p) => p.filter((x) => x.rfid !== c));
  const clearAll = () => { setScanned([]); setUnknownList([]); };

  const submit = async () => {
    if (scanned.length === 0) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/linen/receive`, { rfid_codes: scanned.map((s) => s.rfid) });
      toast.success(`Received ${res.data.received_count} item(s) into warehouse`);
      if (res.data.already_clean_count > 0) {
        toast.info(`${res.data.already_clean_count} already-clean RFID(s) skipped (already at warehouse)`);
      }
      if (res.data.unknown_count > 0) {
        setUnknownList(res.data.unknown);
        toast.warning(`${res.data.unknown_count} unknown RFID(s) — register them in Inventory to track`);
      } else {
        setUnknownList([]);
      }
      setScanned([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Receive failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-xl text-brand-navy flex items-center gap-2">
            <Scan className="h-5 w-5" /> Scan returning items
            <span className="ml-auto"><BleReaderButton light /></span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isInPdaWrapper() ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm flex items-center gap-2">
              <Scan className="h-4 w-4 text-emerald-700" />
              <span className="text-emerald-800">
                Pull trigger — scanned tags appear below
              </span>
            </div>
          ) : (
          <form onSubmit={handleScan} className="flex gap-2">
            <Input
              ref={inputRef}
              data-testid="receiving-scan-input"
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              placeholder="Scan or type RFID, then Enter"
              className="flex-1 font-mono text-lg h-12"
              autoFocus
            />
            <Button type="submit" className="bg-brand-navy hover:bg-brand-navy-hover">Add</Button>
          </form>
          )}
          <ProductSummary scanned={scanned} />
          <ScannedList scanned={scanned} onClearAll={clearAll} />
          <div className="mt-6 flex justify-end">
            <Button
              data-testid="receiving-submit"
              onClick={submit}
              disabled={submitting || scanned.length === 0}
              className="bg-brand-navy hover:bg-brand-navy-hover"
            >
              <Inbox className="h-4 w-4 mr-2" />
              {submitting ? "Receiving..." : `Receive ${scanned.length} item(s)`}
            </Button>
          </div>
        </CardContent>
      </Card>
      {unknownList.length > 0 && (
        <UnknownBanner list={unknownList} />
      )}
    </div>
  );
}

// ---------- Rewash (returned dirty / not washed cleanly) ----------
function RewashView() {
  const [scanInput, setScanInput] = useState("");
  const [scanned, setScanned] = useState([]); // [{rfid, product_id, product_name}]
  const [submitting, setSubmitting] = useState(false);
  const [unknownList, setUnknownList] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [hotelId, setHotelId] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  // Manual items (products with no RFID chip). Populated from the hotel's
  // assigned-products list once a hotel is picked. Shape: pid → { name, qty }.
  const [assigned, setAssigned] = useState([]);
  const [manual, setManual] = useState({});
  const inputRef = useRef(null);
  const inFlightRef = useRef(new Set());

  useEffect(() => {
    (async () => {
      try {
        // Use /api/hotels so each Hotel row is a distinct pick — one owner
        // can have multiple hotels with different addresses/coords, and the
        // rewash order needs to attach to a specific one.
        const res = await axios.get(`${API}/hotels`);
        setHotels(res.data || []);
      } catch (e) {
        toast.error("Failed to load hotels");
      }
    })();
  }, []);

  // When the hotel changes, load the products assigned to that hotel so the
  // operator can enter re-wash quantities for items that have no RFID chip.
  useEffect(() => {
    setManual({});
    if (!hotelId) { setAssigned([]); return; }
    axios.get(`${API}/hotels/${hotelId}/assigned-products`)
      .then((res) => setAssigned(res.data.products || []))
      .catch(() => setAssigned([]));
  }, [hotelId]);

  const manualTotal = Object.values(manual).reduce((s, m) => s + (m.qty || 0), 0);
  const setQty = (pid, name, qty) => {
    setManual((prev) => {
      const next = { ...prev };
      if (!qty || qty <= 0) delete next[pid];
      else next[pid] = { name, qty };
      return next;
    });
  };

  const handleScan = (e) => {
    e?.preventDefault?.();
    const code = scanInput.trim();
    if (!code) return;
    setScanInput("");
    if (scanned.find((s) => s.rfid === code)) {
      toast.warning(`${code} already in list`);
      setTimeout(() => inputRef.current?.focus(), 10);
      return;
    }
    // PDA burst speed: push immediately with no product info, resolve in background.
    setScanned((prev) => {
      if (prev.find((s) => s.rfid === code)) return prev;
      return [...prev, { rfid: code, product_id: null, product_name: null }];
    });
    if (!inFlightRef.current.has(code)) {
      inFlightRef.current.add(code);
      lookupRfid(code).then((entry) => {
        inFlightRef.current.delete(code);
        if (entry.product_id) {
          setScanned((prev) => prev.map((s) => s.rfid === code
            ? { ...s, product_id: entry.product_id, product_name: entry.product_name }
            : s));
        }
      });
    }
    setTimeout(() => inputRef.current?.focus(), 10);
  };

  // Bottom-strip Reset wipes our scanned list too — so "scanned the wrong
  // batch by mistake" doesn't require manual list editing.
  usePdaScanReset(() => { setScanned([]); setUnknownList([]); });

  // Native PDA wrapper: route the "Stop & Send" payload into our scanned list.
  usePdaScan((epcs) => {
    setScanned((prev) => {
      const known = new Set(prev.map((s) => s.rfid));
      const additions = [];
      epcs.forEach((rfid) => {
        if (!known.has(rfid)) {
          known.add(rfid);
          additions.push({ rfid, product_id: null, product_name: null });
          if (!inFlightRef.current.has(rfid)) {
            inFlightRef.current.add(rfid);
            lookupRfid(rfid).then((entry) => {
              inFlightRef.current.delete(rfid);
              if (entry.product_id) {
                setScanned((p) => p.map((s) => s.rfid === rfid
                  ? { ...s, product_id: entry.product_id, product_name: entry.product_name }
                  : s));
              }
            });
          }
        }
      });
      return additions.length ? [...prev, ...additions] : prev;
    });
  });

  const remove = (c) => setScanned((p) => p.filter((x) => x.rfid !== c));
  const clearAll = () => { setScanned([]); setUnknownList([]); };

  const submit = async () => {
    if (scanned.length === 0 && manualTotal === 0) {
      toast.error("Scan at least one RFID or add a manual item first");
      return;
    }
    if (!hotelId) { toast.error("Pick the hotel that returned these"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/linen/rewash`, {
        rfid_codes: scanned.map((s) => s.rfid),
        hotel_id: hotelId,
        delivery_date: deliveryDate,
        notes: notes || null,
        manual_items: Object.entries(manual).map(([product_id, m]) => ({ product_id, quantity: m.qty })),
      });
      toast.success(`Rewash order ${res.data.order_number} created`, {
        description: `${res.data.received_count || 0} received · ${manualTotal ? `${manualTotal} manual · ` : ""}${res.data.hotel_name} · pending, $0`,
      });
      if (res.data.unknown_count > 0) {
        setUnknownList(res.data.unknown);
        toast.warning(`${res.data.unknown_count} unknown RFID(s) — order still created`);
      } else {
        setUnknownList([]);
      }
      setScanned([]);
      setManual({});
      setNotes("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rewash failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-amber-300 bg-amber-50/40">
        <CardHeader>
          <CardTitle className="font-display text-xl text-amber-900 flex items-center gap-2">
            <RotateCcw className="h-5 w-5" /> Rewash — returned dirty
          </CardTitle>
          <p className="text-sm text-amber-900/80 mt-1">
            Scan items the hotel returned because they weren&apos;t washed cleanly. Items go back to the warehouse
            and a <span className="font-semibold">free pending order</span> is auto-created so we re-deliver after re-washing.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Hotel</Label>
              <Select value={hotelId} onValueChange={setHotelId}>
                <SelectTrigger data-testid="rewash-hotel-select"><SelectValue placeholder="Pick the hotel that returned these" /></SelectTrigger>
                <SelectContent>
                  {hotels.map((h) => (
                    <SelectItem key={h.id} value={h.id}>
                      {h.hotel_name || h.owner_email}
                      {h.suburb ? <span className="text-muted-foreground"> · {h.suburb}</span> : null}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Re-delivery date</Label>
              <Input
                type="date"
                data-testid="rewash-date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
              />
            </div>
          </div>

          {isInPdaWrapper() ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm flex items-center gap-2">
              <Scan className="h-4 w-4 text-amber-700" />
              <span className="text-amber-800">
                Pull trigger — scanned tags appear below
              </span>
            </div>
          ) : (
          <form onSubmit={handleScan} className="flex gap-2">
            <Input
              ref={inputRef}
              data-testid="rewash-scan-input"
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              placeholder="Scan returning dirty RFID, then Enter"
              className="flex-1 font-mono text-lg h-12"
            />
            <Button type="submit" className="bg-amber-700 hover:bg-amber-800 text-white">Add</Button>
          </form>
          )}

          <ProductSummary scanned={scanned} accent="amber" />
          <ScannedList scanned={scanned} onClearAll={clearAll} />

          {hotelId && (
            <div className="mt-4">
              <Label className="text-[11px] uppercase tracking-wider font-semibold">
                Manual items (no chip) — products assigned to this hotel
              </Label>
              <div className="mt-1 border border-amber-200 rounded-md bg-white divide-y max-h-72 overflow-y-auto" data-testid="rewash-manual-list">
                {assigned.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-muted-foreground">No products assigned to this hotel.</div>
                ) : assigned.map((p) => (
                  <div key={p.id} className="px-3 py-2 flex items-center gap-3">
                    <span className="text-sm flex-1 truncate">{p.name}</span>
                    <Input
                      type="number"
                      min={0}
                      value={manual[p.id]?.qty ?? ""}
                      onChange={(e) => setQty(p.id, p.name, parseInt(e.target.value || "0", 10))}
                      data-testid={`rewash-qty-${p.id}`}
                      placeholder="0"
                      className="w-24 h-8 text-right"
                    />
                  </div>
                ))}
              </div>
              {manualTotal > 0 && (
                <p className="text-[11px] text-amber-800 mt-1">
                  {manualTotal} manual item(s) queued.
                </p>
              )}
            </div>
          )}

          <div className="mt-4">
            <Label className="text-[11px] uppercase tracking-wider font-semibold">Notes (optional)</Label>
            <Input
              data-testid="rewash-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. coffee stains on king sheets — bag returned 16 May"
            />
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              data-testid="rewash-submit"
              onClick={submit}
              disabled={submitting || !hotelId || (scanned.length === 0 && manualTotal === 0)}
              className="bg-amber-700 hover:bg-amber-800 text-white"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {submitting ? "Creating rewash..." : `Create rewash order (${scanned.length + manualTotal})`}
            </Button>
          </div>
        </CardContent>
      </Card>
      {unknownList.length > 0 && <UnknownBanner list={unknownList} />}
    </div>
  );
}

// ---------- shared bits ----------
function ProductSummary({ scanned, accent = "navy" }) {
  if (scanned.length === 0) return null;
  // Aggregate by product_name; unknowns grouped under "Unknown".
  const totals = scanned.reduce((acc, s) => {
    const key = s.product_name || "__unknown__";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const entries = Object.entries(totals).sort(([a], [b]) => {
    if (a === "__unknown__") return 1;
    if (b === "__unknown__") return -1;
    return a.localeCompare(b);
  });
  const isAmber = accent === "amber";
  const wrapperCls = isAmber
    ? "mt-5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
    : "mt-5 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2";
  return (
    <div data-testid="receiving-product-summary" className={wrapperCls}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-2">
        Scanned by product
      </div>
      <div className="flex flex-wrap gap-2">
        {entries.map(([name, count]) => {
          const unknown = name === "__unknown__";
          return (
            <Badge
              key={name}
              variant={unknown ? "outline" : "secondary"}
              className={
                unknown
                  ? "border-yellow-400 text-yellow-900 bg-yellow-50 font-medium"
                  : "font-medium"
              }
            >
              {unknown ? "Unknown" : name} <span className="ml-1 font-semibold">×{count}</span>
            </Badge>
          );
        })}
      </div>
    </div>
  );
}

function ScannedList({ scanned, onClearAll }) {
  if (scanned.length === 0) {
    return (
      <div className="mt-6 rounded-md border border-dashed border-neutral-200 px-4 py-3 text-sm text-muted-foreground">
        Nothing scanned yet — start scanning and the totals will appear above.
      </div>
    );
  }
  return (
    <div className="mt-6 flex items-center justify-between gap-3 rounded-md border border-neutral-200 bg-white px-4 py-2">
      <div className="text-sm">
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground mr-2">
          Total scanned
        </span>
        <span className="font-bold text-base">{scanned.length}</span>
      </div>
      <Button size="sm" variant="ghost" onClick={onClearAll} className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7">
        <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear
      </Button>
    </div>
  );
}

function UnknownBanner({ list }) {
  return (
    <Card className="border-yellow-300 bg-yellow-50">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2 text-yellow-800">
          <AlertCircle className="h-5 w-5" /> Unknown RFIDs ({list.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-yellow-800 mb-3">
          These codes are not registered. Go to <strong>Inventory</strong> to register them so they appear in future tracking.
        </p>
        <div className="flex flex-wrap gap-2">
          {list.map((c) => (
            <Badge key={c} variant="secondary" className="font-mono">{c}</Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
